<script setup lang="ts">
import { Translation } from 'vue-i18n';

import { PriceGuaranteeSunBanner } from '@apps/main/new-desktop/modules/priceGuarantee';
import { usePreferredTimeStore } from '@apps/main/new-desktop/core/stores/preferredTimeStore';
import { DisneylandCampaignBanner } from '@apps/main/new-desktop/modules/disneylandCampaign';
import type { DisneylandCampaignDetailedBannerData } from '@apps/main/new-desktop/core/models/DisneylandCampaign';

import { AppSvgIcon } from '@libs/ui';

import type { HotelDetailsHeaderData, Stay } from '../../models';
import { useHotelDetailsPanelStore } from '../../stores';
import HotelSelectionSectionLoader from '../loaders/HotelSelectionSectionLoader.vue';

import HotelSelectionSection from './HotelSelectionSection.vue';

interface HotelCallToActionSectionProps {
  headerData: HotelDetailsHeaderData | undefined;
  isHearted: boolean;
  isAvailabilityLoading: boolean;
  stay?: Stay;
  disneylandCampaignBannerData?: DisneylandCampaignDetailedBannerData;
  isDisneylandCampaignHotel?: boolean;
}

const props = defineProps<HotelCallToActionSectionProps>();

interface HotelCallToActionSectionEmits {
  (event: 'continue-with-room'): void;
  (event: 'open-price-guarantee'): void;
  (event: 'scroll-to-rooms'): void;
  (event: 'select-hotel'): void;
  (event: 'unselect-hotel'): void;
  (event: 'open-disneyland-campaign-modal'): void;
}

const emit = defineEmits<HotelCallToActionSectionEmits>();

const hotelDetailsPanelStore = useHotelDetailsPanelStore();
const preferredTimeStore = usePreferredTimeStore();
</script>

<template>
  <div class="hotel-call-to-action-section">
    <template v-if="props.isAvailabilityLoading">
      <HotelSelectionSectionLoader />
      <PriceGuaranteeSunBanner @learn-more="emit('open-price-guarantee')" />
    </template>

    <template v-else>
      <template v-if="props.headerData?.headerText && props.stay">
        <HotelSelectionSection
          :is-hearted="props.isHearted"
          :is-date-selected="preferredTimeStore.preferredDay !== undefined"
          :stay="props.stay"
          :date-range="hotelDetailsPanelStore.footerData?.datesRange"
          :hotel-name="props.headerData.headerText"
          @continue-with-room="emit('continue-with-room')"
          @scroll-to-rooms="emit('scroll-to-rooms')"
          @select-hotel="emit('select-hotel')"
          @unselect-hotel="emit('unselect-hotel')"
        />
        <PriceGuaranteeSunBanner @learn-more="emit('open-price-guarantee')" />
        <DisneylandCampaignBanner
          v-if="disneylandCampaignBannerData && isDisneylandCampaignHotel"
          :banner-data="disneylandCampaignBannerData"
          class="disneyland-campaign-banner"
          @open-modal="emit('open-disneyland-campaign-modal')"
        >
          <template #title>
            <Translation
              class="title"
              keypath="disneyland_campaign.title"
              tag="span"
            >
              <template #hotelName>
                <span>{{ disneylandCampaignBannerData.hotelName }}</span>
              </template>
              <template #amount>
                <span>
                  {{ disneylandCampaignBannerData.giftCardAmount }}
                </span>
              </template>
            </Translation>
          </template>
          <template #content>
            <Translation
              class="content"
              keypath="disneyland_campaign.content"
              tag="span"
            >
              <template #hotelName>
                <span>{{ disneylandCampaignBannerData.hotelName }}</span>
              </template>
              <template #amount>
                <span>
                  {{ disneylandCampaignBannerData.giftCardAmount }}
                </span>
              </template>
            </Translation>
          </template>
        </DisneylandCampaignBanner>
      </template>

      <div v-else class="message">
        <AppSvgIcon name="infoCircle" size="24" />
        <div class="message-inner">
          <div class="title">
            {{
              $t('planner.no_availability.messages.hotel_not_available_title')
            }}
          </div>
          <div class="content">
            {{
              $t('planner.no_availability.messages.hotel_not_available_content')
            }}
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
.hotel-call-to-action-section {
  .message {
    padding: var(--spacing-16);
    background-color: var(--color-brand-summer);
    border-radius: var(--border-radius-12);

    display: flex;
    gap: var(--spacing-12);

    .message-inner {
      display: flex;
      flex-flow: column;
      gap: var(--spacing-8);
      align-items: flex-start;

      .title {
        color: var(--color-neutral-70);
        @include font-heading-5;
      }

      .content {
        color: var(--color-neutral-70);
        @include font-body-m-light;
      }
    }
  }
  .disneyland-campaign-banner {
    margin-top: var(--spacing-16);
  }
}
</style>
